import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InstagramEmbed } from "react-social-media-embed";
import image from "../asset/image.jpg";

const testimonials = [
  {
    quote:
      "Pelumi has a strong product mindset. He asks the right questions early and builds interfaces that feel clear, stable, and easy to use. Working with him always feels structured.",
    name: "Daniel O.",
    role: "Frontend Engineer",
  },

  {
    quote:
      "What stands out about Pelumi is how intentional his work is. He pays attention to details that most people overlook, especially around usability and performance.",
    name: "Kehinde A.",
    role: "Product Manager",
  },

  {
    quote:
      "Pelumi is dependable and communicates clearly throughout the process. You always know where things stand, and the final output matches what was discussed.",
    name: "Sarah I.",
    role: "Founder",
  },

  {
    quote:
      "Pelumi made the entire process feel straightforward. He understood what we needed quickly and delivered a clean, professional website that works exactly as expected.",
    name: "Michael T.",
    role: "Business Owner",
  },

  {
    quote:
      "He doesn't just build what you ask for. He explains better options when needed and keeps the focus on clarity and results.",
    name: "Blessing A.",
    role: "Brand Strategist",
  },
];

const stats = [
  { value: "3+", label: "Years building" },
  { value: "20+", label: "Projects shipped" },
  { value: "10+", label: "Happy clients" },
];

const instagramPosts = [
  "https://www.instagram.com/reel/DYU09fjN0OG/",
  "https://www.instagram.com/reel/DYU09fjN0OG/",
  "https://www.instagram.com/reel/DYU09fjN0OG/",
];

const Aboutme = () => {
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const next = () =>
    setTestimonialIndex((p) => (p + 1) % testimonials.length);

  const prev = () =>
    setTestimonialIndex((p) =>
      p === 0 ? testimonials.length - 1 : p - 1
    );

  return (
    <section className="bg-[#171718] border-b-2 border-[#7C6AE6] pt-[140px] pb-[100px] overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-6">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#7C6AE6] mb-4 block">
            About Me
          </span>

          <h1 className="text-[36px] md:text-[62px] font-semibold uppercase text-white leading-[0.95]">
            Developer.
            <br />
            <span className="text-white/30">
              Designer. Builder.
            </span>
          </h1>
        </motion.div>

        {/* STATS */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap gap-12 mb-24 border-t border-b border-white/10 py-8"
        >
          {stats.map((s) => (
            <div key={s.label}>
              <p className="text-[32px] md:text-[44px] font-bold text-white leading-none">
                {s.value}
              </p>

              <p className="text-[12px] uppercase tracking-widest text-white/40 mt-2">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>

        {/* MAIN CONTENT */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start mb-28"
        >

          {/* IMAGE */}
          <div className="lg:col-span-4">
            <img
              src={image}
              alt="Pelumi"
              className="w-full aspect-[3/4] object-cover border border-white/10 rounded-[24px]"
            />
          </div>

          {/* COPY */}
         <div className="lg:col-span-8">

  <div className="space-y-8">

    <p className="text-white text-[20px] md:text-[24px] leading-relaxed font-medium">
      I'm Pelumi, a Front End and WordPress developer with a background
      in Computer Science, focused on building digital products that feel
      clean, intuitive, and genuinely useful to the people using them.
    </p>

    <p className="text-white/65 text-lg leading-relaxed">
      I got into development through curiosity around how products work,
      not just visually, but structurally. That interest eventually led
      me to study Computer Science at Redeemer's University, where I built
      a stronger understanding of systems, logic, problem solving, and
      how technology shapes user experiences.
    </p>

    <p className="text-white/65 text-lg leading-relaxed">
      Over the past few years, I've worked with startups, founders,
      agencies, and growing businesses to design and build websites that
      do more than look impressive. My focus is usually on clarity,
      usability, responsiveness, and making sure the product communicates
      properly with the people using it.
    </p>

    <p className="text-white/65 text-lg leading-relaxed">
      I enjoy the process of turning rough ideas into structured digital
      experiences. From planning layouts and user flows to building smooth
      responsive interfaces, I pay close attention to the details that
      make products feel polished and easy to use.
    </p>

    <p className="text-white/65 text-lg leading-relaxed">
      Outside development, I'm deeply interested in product thinking,
      interface design, content creation, and the way people interact with
      technology daily. I also enjoy sharing what I learn through content,
      especially around front end development, UX observations, and
      building products in public.
    </p>

    <div className="pt-4 border-t border-white/10">
      <p className="text-white text-xl md:text-2xl font-semibold leading-relaxed">
        I care about building products that feel thoughtful, easy to use,
        and memorable long after someone leaves the screen.
      </p>
    </div>

  </div>

</div>
        </motion.div>

        {/* CONTENT SECTION */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-28"
        >

          <div className="mb-12">
            <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#7C6AE6] block mb-4">
              Content & Process
            </span>

            <h2 className="text-white text-[30px] md:text-[42px] font-semibold leading-tight mb-5">
              I share what I learn while building.
            </h2>

            <p className="text-white/55 text-lg leading-relaxed max-w-3xl">
              I create content around interfaces, front-end development,
              product thinking, and real-world client work.
            </p>
          </div>

          {/* INSTAGRAM */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

            {instagramPosts.map((url, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: i * 0.1,
                }}
                className="
                  rounded-[24px]
overflow-hidden
border border-white/10
bg-white/[0.03]
p-0
flex justify-center
                "
              >
             <div className="overflow-hidden rounded-[18px]">
  <InstagramEmbed
    url={url}
    width="100%"
    captioned={false}
  />
</div>
              </motion.div>
            ))}

          </div>

          <a
            href="https://www.instagram.com/buildwithpelumi"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex mt-8 items-center gap-2 text-[13px] font-bold uppercase tracking-widest text-[#7C6AE6] hover:text-white transition-colors"
          >
            Follow on Instagram →
          </a>

        </motion.div>

        {/* HUMAN SECTION */}
      <motion.div
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.6 }}
  className="mb-28"
>

  <div className="flex items-center justify-between mb-12">

    <div>
  

      <h2 className="text-white text-[28px] md:text-[40px] font-semibold leading-tight">
        Outside Development
      </h2>
    </div>

  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

    {[
      "The Office is probably my most rewatched sitcom.",
      "Barcelona fan. Watching Lamine Yamal right now feels unreal.",
   "I watch a lot of YouTube.",
      "A good shawarma can genuinely improve my day.",
    ].map((text, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          duration: 0.4,
          delay: i * 0.08,
        }}
        className="
          group
          border
          border-white/10
          bg-white/[0.03]
          rounded-[24px]
          p-7

          transition-all
          duration-300
        "
      >

        <div className="flex items-start gap-4">

  

          <p className="text-white/80 text-lg leading-relaxed">
            {text}
          </p>

        </div>

      </motion.div>
    ))}

  </div>

</motion.div>

        {/* TESTIMONIALS */}
        <div className="border-t border-white/10 pt-16">

          <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#7C6AE6] mb-8 block">
            What people say
          </span>

          <div className="relative border border-white/10 bg-white/[0.03] p-8 md:p-14 rounded-[24px]">

            <AnimatePresence mode="wait">
              <motion.div
                key={testimonialIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >

                <span className="block text-[52px] text-[#7C6AE6] leading-none mb-6">
                  "
                </span>

                <p className="text-white text-[18px] md:text-[22px] leading-relaxed font-medium max-w-3xl">
                  {testimonials[testimonialIndex].quote}
                </p>

                <div className="mt-8">
                  <p className="text-white font-semibold">
                    {testimonials[testimonialIndex].name}
                  </p>

                  <p className="text-white/40 text-sm">
                    {testimonials[testimonialIndex].role}
                  </p>
                </div>

              </motion.div>
            </AnimatePresence>

            <div className="mt-10 flex items-center justify-between">

              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTestimonialIndex(i)}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      i === testimonialIndex
                        ? "bg-[#7C6AE6] w-8"
                        : "bg-white/20 w-4"
                    }`}
                  />
                ))}
              </div>

              <div className="flex gap-3">

                <button
                  onClick={prev}
                  className="w-10 h-10 rounded-full border border-white/20 text-white/60 hover:border-[#7C6AE6] hover:text-[#7C6AE6] transition-colors"
                >
                  ←
                </button>

                <button
                  onClick={next}
                  className="w-10 h-10 rounded-full border border-white/20 text-white/60 hover:border-[#7C6AE6] hover:text-[#7C6AE6] transition-colors"
                >
                  →
                </button>

              </div>

            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default Aboutme;